import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  DateUtilsService,
  DEFAULT_SEMESTERS,
  type SemesterOption,
} from '../../../services/date-utils.service';

@Component({
  selector: 'app-periodo-semestral',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './periodo-semestral.component.html',
  styleUrls: ['./periodo-semestral.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PeriodoSemestralComponent implements OnInit {
  @Input() years: number[] = [];
  @Input() semesters: SemesterOption[] = [];
  /** ISO `yyyy-MM-dd` o vacío */
  @Input() maxDate: string | null = null;
  /** ISO `yyyy-MM-dd` o vacío; si es `null`/vacío se usa la mínima del sistema */
  @Input() minDate: string | null = null;

  @Input() selectedYear: number | null = null;
  @Output() selectedYearChange = new EventEmitter<number | null>();

  @Input() selectedSemester: SemesterOption | null = null;
  @Output() selectedSemesterChange = new EventEmitter<SemesterOption | null>();

  constructor(private readonly dateUtilsService: DateUtilsService) {}

  ngOnInit(): void {
    const currentYear = this.dateUtilsService.getCurrentYear();
    const currentSemester = this.dateUtilsService.getCurrentSemesterNumber();
    const minIso = this.dateUtilsService.getMinDate(this.minDate);
    const minYear = this.dateUtilsService.parseIsoToLocalDate(minIso).getFullYear();

    let maxYear: number;
    if (this.maxDate?.trim()) {
      const maxD = new Date(this.maxDate.trim());
      if (!Number.isNaN(maxD.getTime())) {
        maxYear = maxD.getFullYear();
      } else {
        maxYear = currentYear;
      }
    } else {
      maxYear = currentYear;
    }

    this.years = this.dateUtilsService.buildYearsRange(minYear, maxYear);

    if (!this.semesters.length) {
      this.semesters = [...DEFAULT_SEMESTERS];
    }

    const parentHadYear = this.selectedYear !== null;
    const parentHadSemester = this.selectedSemester !== null;

    if (this.selectedYear === null) {
      this.selectedYear = Math.min(
        Math.max(currentYear, minYear),
        maxYear,
      );
    }

    this.semesters = this.dateUtilsService.buildSemestersRange(
      this.selectedYear!,
      this.minDate,
    );

    if (this.selectedSemester === null) {
      this.selectedSemester =
        this.semesters.find((s) => s.number === currentSemester) ??
        this.semesters[this.semesters.length - 1] ??
        null;
    }

    if (!parentHadYear || !parentHadSemester) {
      queueMicrotask(() => {
        if (!parentHadYear) {
          this.selectedYearChange.emit(this.selectedYear);
        }
        if (!parentHadSemester) {
          this.selectedSemesterChange.emit(this.selectedSemester);
        }
      });
    }
  }

  onYearModelChange(value: number | null): void {
    this.selectedYear = value;
    this.selectedYearChange.emit(this.selectedYear);
    this.semesters = this.dateUtilsService.buildSemestersRange(
      this.selectedYear!,
      this.minDate,
    );
    this.selectedSemester = this.semesters[0] ?? null;
    this.selectedSemesterChange.emit(this.selectedSemester);
  }

  onSemesterModelChange(value: SemesterOption | null): void {
    this.selectedSemester = value;
    this.selectedSemesterChange.emit(this.selectedSemester);
  }
}
